import { useToastStatus as ToastStatusGlobal } from '@/components/toastStatusGlobal';
import { moveOpeningHoursInitialState } from '@/modules/move/entities/constants/move';
import { MOVE_SHEET_COLUMN } from '@/modules/move/entities/constants/moveSheetColumn';
import { MOVE_STATUS } from '@/modules/move/entities/enums/move';
import { MoveNormalized, MoveOpeningHours } from '@/modules/move/entities/interfaces/move';
import { transformEditedRowToMoveFileColumns } from '@/modules/move/hooks/useMoveEdition/utils/transformEditedRowToMoveFileColumns';
import { MoveService } from '@/modules/move/services';
import { useHistoryNavigator } from '@/navigation';
import { MOVE_FILE_TYPE } from '@/pages/moves/shared/constants';
import { mountFile } from '@/pages/moves/utils/mountFile';
import { movesValidationService } from '@/services/files';
import {
  MovesValidationErrorDetail,
  movesValidationErrorInitialState,
} from '@/services/files/endpoints/moves';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { decodedJwtToken } from '@/utils/authentication';
import { getMessageError } from '@/utils/getMessageError';
import { get, has } from 'lodash';
import { useCallback, useState } from 'react';

export enum MOVE_EDITION_STATUS {
  EDITION = 'edition',
  FINISHED = 'finished',
}

export interface EditedRow {
  fields: Partial<Record<MOVE_SHEET_COLUMN, unknown>>;
  status: MOVE_EDITION_STATUS;
  accountable?: boolean;
}

export type EditedRowByNumber = Record<string, EditedRow>;

interface UseMoveEdition {
  move?: MoveNormalized;
}

const moveService = new MoveService();

export const useMoveEdition = ({ move }: UseMoveEdition) => {
  const [editedRowByLine, setEditedRowByNumber] = useState<EditedRowByNumber>({});
  const [errorDetails, setErrorDetails] = useState<MovesValidationErrorDetail[]>([]);
  const { toast } = ToastStatusGlobal();
  const [loading, setLoading] = useState(false);
  const [openingHours, setOpeningHours] = useState<MoveOpeningHours>(moveOpeningHoursInitialState);
  const navigate = useHistoryNavigator();
  const { companyId } = useCompany();
  const [showDialogOpeningHoursMessage, setShowDialogOpeningHoursMessage] = useState(false);

  const updateEditedRow = useCallback((line: string, data: Partial<EditedRow>) => {
    setEditedRowByNumber((current) => {
      current[line] = {
        ...current[line],
        ...data,
      };

      return current;
    });
  }, []);

  const getFormDataValues = useCallback(async () => {
    if (!move) {
      return;
    }

    const formData = new FormData();
    const decodedToken = decodedJwtToken();

    const dataToMove = Object.entries(editedRowByLine).map(([lineNumber, data]) =>
      transformEditedRowToMoveFileColumns(data, lineNumber),
    );

    const sheet = mountFile(dataToMove, MOVE_FILE_TYPE.CREATION, 'move-edition');

    formData.append('userName', decodedToken.user.name);
    formData.append('userId', decodedToken.sub);
    formData.append('userEmail', decodedToken.user.email);
    formData.append('scope', 'company');
    formData.append('companyId', move.companyId);
    formData.append('providerId', move.providerId);
    formData.append('movimentationType', 'creation');
    formData.append('fileOriginId', move.id);
    formData.append('files', sheet);
    formData.append('skipRequiredValidations', 'CPF');

    return formData;
  }, [editedRowByLine, move]);

  const saveEditedRow = useCallback(async () => {
    if (!move) {
      return;
    }

    setLoading(true);

    try {
      const formData = await getFormDataValues();
      const response = (await movesValidationService.create(formData)) as MoveOpeningHours;
      await moveService.updateStatus(move?.id, {
        status: response.isOpeningHours ? MOVE_STATUS.IN_PROCESS : MOVE_STATUS.NOT_INITIALIZED,
      });

      if (response && !response.isOpeningHours) {
        setOpeningHours(response);
        setShowDialogOpeningHoursMessage(true);
      } else {
        navigate.push(`/companies/${companyId}/beneficiaries/moves`);
        toast('Pronto!', 'Movimentação editada com sucesso', 'success');
      }
    } catch (error) {
      console.error(error);
      const messageError = getMessageError({
        error,
        messageDefault: 'Erro ao solicitar edição.',
      });
      if (has(error, 'response.data.details')) {
        setErrorDetails(
          get(error, 'response.data.details', movesValidationErrorInitialState.details),
        );
      }
      toast('Puxa!', messageError, 'warning');
    } finally {
      setLoading(false);
    }
  }, [companyId, getFormDataValues, move, navigate, toast]);

  return {
    updateEditedRow,
    editedRowByLine,
    saveEditedRow,
    setErrorDetails,
    errorDetails,
    loading,
    openingHours,
    showDialogOpeningHoursMessage,
    setShowDialogOpeningHoursMessage,
  };
};
